import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Col, Input, Row } from "antd";
import Container from "../../components/container/Container";
import { apiUrl } from "../../config/config";

const { Search } = Input;

const TEMPLATE_CATEGORY_ENDPOINTS = [
  `${apiUrl}/api/template-categories-active-assigned`,
  `${apiUrl}/api/template-categories`,
  `${apiUrl}/api/template_categories`,
];

const STATIC_LABELS = {
  products: "All Products",
  brand: "Brands",
  catalogsearch: "Catalog Search",
  result: "Results",
  templates: "Templates",
  cart: "Cart",
  checkout: "Checkout",
  blogs: "Blogs",
  "product-delivery": "Product Delivery",
  login: "Login",
  signup: "Sign Up",
  "update-profile": "Update Profile",
  about: "About",
  explore: "Explore",
  passion: "Passion",
  agreement: "Agreement",
  story: "Story",
  policy: "Policy",
  solution: "Solution",
  accounts: "My Account",
  invoice: "Invoice",
  workorderpage: "Work Order",
  Orderplaced: "Order Placed",
  "design-preview": "Design Preview",
};

const decodeSegment = (segment = "") => {
  try {
    return decodeURIComponent(segment);
  } catch (error) {
    return segment;
  }
};

const formatSegment = (segment = "") => {
  const decoded = decodeSegment(segment)
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!decoded) return "";

  return decoded.replace(/\b\w/g, (char) => char.toUpperCase());
};

const isNumericSegment = (segment = "") => /^\d+$/.test(segment);

const getTemplateCategoryList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.categories)) return payload.categories;
  if (Array.isArray(payload?.template_categories)) return payload.template_categories;
  if (Array.isArray(payload?.templateCategories)) return payload.templateCategories;
  return [];
};

const parseMaybeJsonArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const getTemplateSubcategories = (category) => {
  const possibleLists = [
    category?.subcategories,
    category?.subCategories,
    category?.sub_categories,
    category?.subcategory,
    category?.sub_category,
    category?.assigned_subcategories,
    category?.assignedSubcategories,
    category?.assigned_sub_categories,
    category?.template_category_subcategories,
    category?.templateCategorySubcategories,
    category?.template_subcategories,
    category?.templateSubcategories,
    category?.template_sub_categories,
  ];

  for (const list of possibleLists) {
    const parsedList = parseMaybeJsonArray(list);
    if (parsedList.length > 0) return parsedList;
  }

  return [];
};

const getTemplateItemName = (item) =>
  item?.template_name ||
  item?.name ||
  item?.title ||
  item?.subcategory_name ||
  item?.sub_category_name ||
  "";

const buildTemplateCrumbs = (segments, templateCategories) => {
  const crumbs = [{ label: "Home", to: "/" }, { label: "Templates", to: "/templates" }];
  const categoryId = segments[1];
  const subcategoryId = segments[2];

  if (categoryId) {
    const category = templateCategories.find(
      (item) => String(item.id) === String(categoryId)
    );
    crumbs.push({
      label: getTemplateItemName(category) || `Category ${categoryId}`,
      to: `/templates/${categoryId}`,
    });

    if (subcategoryId) {
      const subcategory = getTemplateSubcategories(category).find(
        (item) => String(item.id) === String(subcategoryId)
      );
      crumbs.push({
        label: getTemplateItemName(subcategory) || `Subcategory ${subcategoryId}`,
        to: `/templates/${categoryId}/${subcategoryId}`,
      });
    }
  }

  return crumbs;
};

const buildProductCrumbs = (segments) => {
  const crumbs = [{ label: "Home", to: "/" }, { label: "All Products", to: "/products" }];

  if (segments[1] === "brand") {
    crumbs.push({ label: "Brands", to: "/products/brand" });

    if (segments[2]) {
      crumbs.push({
        label: formatSegment(segments[2]),
        to: `/products/brand/${segments[2]}`,
      });
    }

    if (segments[3]) {
      crumbs.push({
        label: formatSegment(segments[3]),
        to: `/products/brand/${segments[2]}/${segments[3]}/${segments[4] || ""}`,
      });
    }

    return crumbs;
  }

  if (segments[1]) {
    crumbs.push({
      label: formatSegment(segments[1]),
      to: `/products/${segments[1]}/${segments[2] || ""}`,
    });
  }

  if (segments[3]) {
    crumbs.push({
      label: formatSegment(segments[3]),
      to: `/products/${segments[1]}/${segments[2]}/${segments[3]}/${segments[4] || ""}`,
    });
  }

  if (segments[5]) {
    crumbs.push({
      label: formatSegment(segments[5]),
      to: `/${segments.join("/")}`,
    });
  }

  return crumbs;
};

const buildDesignPreviewCrumbs = (segments) => {
  const crumbs = [{ label: "Home", to: "/" }];
  const designName = segments[2];

  if (designName) {
    crumbs.push({
      label: formatSegment(designName),
      to: `/${segments.join("/")}`,
    });
  }

  return crumbs;
};

const buildDefaultCrumbs = (segments) => {
  const crumbs = [{ label: "Home", to: "/" }];

  segments.forEach((segment, index) => {
    if (isNumericSegment(segment)) return;

    const to = `/${segments.slice(0, index + 1).join("/")}`;
    crumbs.push({
      label: STATIC_LABELS[segment] || formatSegment(segment),
      to,
    });
  });

  return crumbs;
};

const Breadcrumb = ({ search }) => {
  const location = useLocation();
  const [templateCategories, setTemplateCategories] = useState([]);

  const segments = useMemo(
    () => location.pathname.split("/").filter(Boolean),
    [location.pathname]
  );

  useEffect(() => {
    if (segments[0] !== "templates") return;

    const controller = new AbortController();

    const fetchTemplateCategories = async () => {
      for (const endpoint of TEMPLATE_CATEGORY_ENDPOINTS) {
        try {
          const response = await fetch(endpoint, { signal: controller.signal });
          if (!response.ok) continue;

          const payload = await response.json();
          setTemplateCategories(getTemplateCategoryList(payload));
          return;
        } catch (error) {
          if (error.name === "AbortError") return;
        }
      }
    };

    fetchTemplateCategories();

    return () => controller.abort();
  }, [segments]);

  const breadcrumbs = useMemo(() => {
    if (segments.length === 0) return [];

    if (segments[0] === "templates") {
      return buildTemplateCrumbs(segments, templateCategories);
    }

    if (segments[0] === "products") {
      return buildProductCrumbs(segments);
    }

    if (segments[0] === "design-preview") {
      return buildDesignPreviewCrumbs(segments);
    }

    if (segments.length === 2 && isNumericSegment(segments[1])) {
      return [
        { label: "Home", to: "/" },
        { label: formatSegment(segments[0]), to: `/${segments.join("/")}` },
      ];
    }

    return buildDefaultCrumbs(segments);
  }, [segments, templateCategories]);

  const onSearch = (value) => console.log(value);

  if (breadcrumbs.length <= 1 && !search) {
    return null;
  }

  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <Container>
        <Row gutter={[20, 20]} justify="space-between" align="middle">
          <Col xs={24} sm={24} md={24} lg={search ? 12 : 24}>
            <ol className="breadcrumb-list">
              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;

                return (
                  <li className="breadcrumb-item" key={`${crumb.to}-${index}`}>
                    {isLast ? (
                      <span className="breadcrumb-link disabled" aria-current="page">
                        {crumb.label}
                      </span>
                    ) : (
                      <NavLink className="breadcrumb-link" to={crumb.to}>
                        {crumb.label}
                      </NavLink>
                    )}
                  </li>
                );
              })}
            </ol>
          </Col>

          {search && (
            <Col xs={24} sm={24} md={24} lg={8}>
              <Search
                className="custom-search"
                placeholder="search"
                onSearch={onSearch}
              />
            </Col>
          )}
        </Row>
      </Container>
    </nav>
  );
};

export default Breadcrumb;
